import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useInView } from "react-intersection-observer";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import { FaFilter } from "react-icons/fa";

import { fetchPremiumPayments } from "../../utils/apiUtils";
import { loadAccountHccIdentificationNumber } from "../../utils/optionLoaderUtils";
import { customStyles } from "../../utils/styleUtils";
import SharedTable from "../benefitPlan/SharedTable";
import "./PremiumPayments.scss";

const PAGE_SIZE = 40;

const accountFromSearchParams = (params) => {
    const accountKey = params.get("accountKey");
    if (!accountKey) return null;
    const key = Number(accountKey);
    if (!Number.isFinite(key)) return null;
    const hcc = params.get("accountHccId") || "";
    const name = params.get("accountName") || "";
    const label = name && hcc
        ? `${name} (${hcc})`
        : name || hcc || `Account ${key}`;
    return {
        value: hcc || key,
        label,
        additional: { accountKey: key, accountHccId: hcc || undefined, accountName: name || undefined },
    };
};

const PAYMENT_TYPE_OPTIONS = [
    { value: "all", label: "All types" },
    { value: "Check", label: "Check" },
    { value: "EFT", label: "EFT" },
    { value: "Wire", label: "Wire" },
    { value: "ACH", label: "ACH" },
    { value: "Other", label: "Other" },
];

const RECONCILED_OPTIONS = [
    { value: "all", label: "All reconciliation" },
    { value: "Y", label: "Reconciled" },
    { value: "N", label: "Not reconciled" },
];

const REVERSAL_OPTIONS = [
    { value: "all", label: "All (incl. reversals)" },
    { value: "N", label: "Exclude reversals" },
    { value: "Y", label: "Reversals only" },
];

const ALLOCATION_OPTIONS = [
    { value: "all", label: "All allocations" },
    { value: "unallocated", label: "Unallocated" },
    { value: "partial", label: "Partially allocated" },
    { value: "allocated", label: "Fully allocated" },
];

/** Server-side sorts — indexed PREMIUM_PAYMENT_FACT columns in hrp-adapter. */
const SORT_OPTIONS = [
    { value: "paymentKey:desc", label: "Payment # (newest)", sortBy: "paymentKey", sortDir: "desc" },
    { value: "paymentKey:asc", label: "Payment # (oldest)", sortBy: "paymentKey", sortDir: "asc" },
    { value: "paymentDate:desc", label: "Payment date (newest)", sortBy: "paymentDate", sortDir: "desc" },
    { value: "paymentDate:asc", label: "Payment date (oldest)", sortBy: "paymentDate", sortDir: "asc" },
    { value: "accountKey:asc", label: "Account key (A→Z)", sortBy: "accountKey", sortDir: "asc" },
    { value: "accountKey:desc", label: "Account key (Z→A)", sortBy: "accountKey", sortDir: "desc" },
];

const toIsoDate = (date) => {
    if (!date) return undefined;
    const d = new Date(date);
    const tzOffsetMs = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffsetMs).toISOString().slice(0, 10);
};

const PremiumPayments = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [account, setAccount] = useState(() => accountFromSearchParams(searchParams));
    const [asOfDate, setAsOfDate] = useState(() => new Date());
    const [search, setSearch] = useState("");
    const [searchApplied, setSearchApplied] = useState("");
    const [paymentTypeFilter, setPaymentTypeFilter] = useState(PAYMENT_TYPE_OPTIONS[0]);
    const [reconciledFilter, setReconciledFilter] = useState(RECONCILED_OPTIONS[0]);
    const [reversalFilter, setReversalFilter] = useState(REVERSAL_OPTIONS[0]);
    const [allocationFilter, setAllocationFilter] = useState(ALLOCATION_OPTIONS[0]);
    const [sort, setSort] = useState(SORT_OPTIONS[0]);
    const [showMoreColumns, setShowMoreColumns] = useState(false);
    const [showMoreFilters, setShowMoreFilters] = useState(false);

    const { ref, inView } = useInView();
    const asOf = toIsoDate(asOfDate);
    const accountKey = account?.additional?.accountKey;

    const moreFilterCount = useMemo(() => {
        let n = 0;
        if (paymentTypeFilter?.value && paymentTypeFilter.value !== "all") n += 1;
        if (reconciledFilter?.value && reconciledFilter.value !== "all") n += 1;
        if (reversalFilter?.value && reversalFilter.value !== "all") n += 1;
        if (allocationFilter?.value && allocationFilter.value !== "all") n += 1;
        if (sort?.value && sort.value !== SORT_OPTIONS[0].value) n += 1;
        return n;
    }, [paymentTypeFilter, reconciledFilter, reversalFilter, allocationFilter, sort]);

    useEffect(() => {
        if (moreFilterCount > 0) setShowMoreFilters(true);
    }, [moreFilterCount]);

    useEffect(() => {
        const next = accountFromSearchParams(searchParams);
        if (next?.additional?.accountKey !== account?.additional?.accountKey) {
            setAccount(next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams.get("accountKey")]);

    const onAccountChange = (opt) => {
        setAccount(opt);
        const next = new URLSearchParams(searchParams);
        if (opt?.additional?.accountKey) {
            next.set("accountKey", String(opt.additional.accountKey));
            if (opt.additional.accountHccId) next.set("accountHccId", opt.additional.accountHccId);
            else next.delete("accountHccId");
            if (opt.additional.accountName) next.set("accountName", opt.additional.accountName);
            else next.delete("accountName");
        } else {
            next.delete("accountKey");
            next.delete("accountHccId");
            next.delete("accountName");
        }
        setSearchParams(next, { replace: true });
    };

    const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        error,
        status,
    } = useInfiniteQuery({
        queryKey: [
            "premium-payments",
            accountKey || null,
            asOf,
            searchApplied,
            paymentTypeFilter?.value || "all",
            reconciledFilter?.value || "all",
            reversalFilter?.value || "all",
            allocationFilter?.value || "all",
            sort?.sortBy || "paymentKey",
            sort?.sortDir || "desc",
        ],
        queryFn: ({ pageParam = 0 }) =>
            fetchPremiumPayments({
                accountKey: accountKey || undefined,
                asOfDate: asOf,
                limit: PAGE_SIZE,
                offset: pageParam,
                q: searchApplied || undefined,
                paymentType:
                    paymentTypeFilter?.value && paymentTypeFilter.value !== "all"
                        ? paymentTypeFilter.value
                        : undefined,
                reconciled:
                    reconciledFilter?.value && reconciledFilter.value !== "all"
                        ? reconciledFilter.value
                        : undefined,
                reversal:
                    reversalFilter?.value && reversalFilter.value !== "all"
                        ? reversalFilter.value
                        : undefined,
                allocation:
                    allocationFilter?.value && allocationFilter.value !== "all"
                        ? allocationFilter.value
                        : undefined,
                sortBy: sort?.sortBy || "paymentKey",
                sortDir: sort?.sortDir || "desc",
            }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) => {
            const loaded = pages.reduce((sum, p) => sum + (p?.items?.length || 0), 0);
            const total = lastPage?.total ?? 0;
            if (loaded >= total) return undefined;
            if ((lastPage?.items?.length || 0) < PAGE_SIZE) return undefined;
            return loaded;
        },
        staleTime: 0,
        retry: false,
    });

    useEffect(() => {
        if (inView && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const items = useMemo(
        () =>
            (data?.pages || []).flatMap((p) => p?.items || []).map((r) => ({
                ...r,
                premiumPaymentFactKey: r.key,
                allocationCount: r.allocationCount ?? 0,
            })),
        [data],
    );

    let columns = [
        "premiumPaymentFactKey",
        "paymentNumber",
        "accountHccId",
        "accountName",
        "paymentType",
        "paymentDate",
        "amount",
        "allocatedAmount",
        "allocationCount",
    ];

    if (showMoreColumns) {
        columns = [
            ...columns,
            "receiptDate",
            "processDate",
            "sentForBillKey",
            "transactNum",
            "isReconciled",
            "isReversal",
        ];
    }

    const clearFilters = () => {
        setAccount(null);
        setAsOfDate(new Date());
        setSearch("");
        setSearchApplied("");
        setPaymentTypeFilter(PAYMENT_TYPE_OPTIONS[0]);
        setReconciledFilter(RECONCILED_OPTIONS[0]);
        setReversalFilter(REVERSAL_OPTIONS[0]);
        setAllocationFilter(ALLOCATION_OPTIONS[0]);
        setSort(SORT_OPTIONS[0]);
        setSearchParams({}, { replace: true });
    };

    const onDetailHandler = async (row) => {
        const paymentKey = row.premiumPaymentFactKey || row.key;
        const qs = asOf ? `?asOf=${encodeURIComponent(asOf)}` : "";
        window.open(`/finance/premium-payments/${paymentKey}${qs}`, "_blank", "noopener,noreferrer");
        return {};
    };

    return (
        <div className="container premium-payments">
            <p className="title">Premium Payments</p>
            <p className="sub-title">
                Inbound cash received from accounts (premium payments) and how they allocate to
                bills. For outbound claim checks, use Provider Payments.
            </p>

            <div className="premium-payments__controls">
                <div className="premium-payments__filters">
                    <div className="premium-payments__field premium-payments__field--account">
                        <label>Account</label>
                        <AsyncPaginate
                            placeholder="All accounts"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            debounceTimeout={300}
                            value={account}
                            loadOptions={loadAccountHccIdentificationNumber}
                            onChange={onAccountChange}
                            menuPortalTarget={
                                typeof document !== "undefined" ? document.body : null
                            }
                            styles={{
                                ...customStyles,
                                menuPortal: (base) => ({ ...base, zIndex: 200 }),
                            }}
                            isClearable
                        />
                    </div>
                    <div className="premium-payments__field premium-payments__field--date">
                        <label>As of</label>
                        <DatePicker
                            selected={asOfDate}
                            onChange={(d) => {
                                setAsOfDate(d || new Date());
                            }}
                            dateFormat="yyyy-MM-dd"
                            placeholderText="As of date"
                            todayButton="Today"
                            className="date-picker"
                            wrapperClassName="date-picker-wrapper"
                            portalId="root"
                            popperPlacement="bottom-start"
                            popperClassName="date-picker-popper"
                            popperProps={{ strategy: "fixed" }}
                        />
                    </div>
                    <div className="premium-payments__field premium-payments__field--search">
                        <label>Search</label>
                        <div className="premium-payments__search-combo">
                            <input
                                type="text"
                                className="premium-payments__search"
                                placeholder="Payment #, key, account HCC, name"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") setSearchApplied(search.trim());
                                }}
                            />
                            <button
                                type="button"
                                className="premium-payments__search-btn"
                                onClick={() => setSearchApplied(search.trim())}
                            >
                                Search
                            </button>
                        </div>
                    </div>
                </div>

                {showMoreFilters ? (
                    <div
                        className="premium-payments__filters premium-payments__filters--more"
                        role="region"
                        aria-label="More filters"
                    >
                        <div className="premium-payments__field">
                            <label>Payment type</label>
                            <Select
                                className="react-select-container"
                                classNamePrefix="react-select"
                                options={PAYMENT_TYPE_OPTIONS}
                                value={paymentTypeFilter}
                                onChange={(opt) =>
                                    setPaymentTypeFilter(opt || PAYMENT_TYPE_OPTIONS[0])
                                }
                                isSearchable={false}
                                menuPortalTarget={
                                    typeof document !== "undefined" ? document.body : null
                                }
                                styles={{
                                    ...customStyles,
                                    menuPortal: (base) => ({ ...base, zIndex: 200 }),
                                }}
                            />
                        </div>
                        <div className="premium-payments__field">
                            <label>Reconciled</label>
                            <Select
                                className="react-select-container"
                                classNamePrefix="react-select"
                                options={RECONCILED_OPTIONS}
                                value={reconciledFilter}
                                onChange={(opt) =>
                                    setReconciledFilter(opt || RECONCILED_OPTIONS[0])
                                }
                                isSearchable={false}
                                menuPortalTarget={
                                    typeof document !== "undefined" ? document.body : null
                                }
                                styles={{
                                    ...customStyles,
                                    menuPortal: (base) => ({ ...base, zIndex: 200 }),
                                }}
                            />
                        </div>
                        <div className="premium-payments__field">
                            <label>Reversal</label>
                            <Select
                                className="react-select-container"
                                classNamePrefix="react-select"
                                options={REVERSAL_OPTIONS}
                                value={reversalFilter}
                                onChange={(opt) => setReversalFilter(opt || REVERSAL_OPTIONS[0])}
                                isSearchable={false}
                                menuPortalTarget={
                                    typeof document !== "undefined" ? document.body : null
                                }
                                styles={{
                                    ...customStyles,
                                    menuPortal: (base) => ({ ...base, zIndex: 200 }),
                                }}
                            />
                        </div>
                        <div className="premium-payments__field">
                            <label>Allocation</label>
                            <Select
                                className="react-select-container"
                                classNamePrefix="react-select"
                                options={ALLOCATION_OPTIONS}
                                value={allocationFilter}
                                onChange={(opt) =>
                                    setAllocationFilter(opt || ALLOCATION_OPTIONS[0])
                                }
                                isSearchable={false}
                                menuPortalTarget={
                                    typeof document !== "undefined" ? document.body : null
                                }
                                styles={{
                                    ...customStyles,
                                    menuPortal: (base) => ({ ...base, zIndex: 200 }),
                                }}
                            />
                        </div>
                        <div className="premium-payments__field">
                            <label>Sort</label>
                            <Select
                                className="react-select-container"
                                classNamePrefix="react-select"
                                options={SORT_OPTIONS}
                                value={sort}
                                onChange={(opt) => setSort(opt || SORT_OPTIONS[0])}
                                isSearchable={false}
                                menuPortalTarget={
                                    typeof document !== "undefined" ? document.body : null
                                }
                                styles={{
                                    ...customStyles,
                                    menuPortal: (base) => ({ ...base, zIndex: 200 }),
                                }}
                            />
                        </div>
                    </div>
                ) : null}

                <div className="premium-payments__toolbar">
                    <div className="premium-payments__toolbar-left">
                        <button
                            type="button"
                            className={`premium-payments__text-btn premium-payments__text-btn--filter${
                                showMoreFilters
                                    ? " premium-payments__text-btn--filter-active"
                                    : ""
                            }`}
                            onClick={() => setShowMoreFilters((v) => !v)}
                            aria-pressed={showMoreFilters}
                            aria-expanded={showMoreFilters}
                        >
                            <FaFilter aria-hidden />
                            {showMoreFilters ? "Hide filters" : "More filters"}
                            {moreFilterCount > 0 ? (
                                <span className="premium-payments__filter-badge">
                                    {moreFilterCount}
                                </span>
                            ) : null}
                        </button>
                        <button
                            type="button"
                            className="premium-payments__text-btn premium-payments__text-btn--danger"
                            onClick={clearFilters}
                        >
                            Clear filters
                        </button>
                    </div>
                    <button
                        type="button"
                        className="premium-payments__text-btn"
                        onClick={() => setShowMoreColumns((v) => !v)}
                    >
                        {showMoreColumns ? "Less columns" : "More columns"}
                    </button>
                </div>
            </div>

            {status === "error" ? (
                <span className="danger">
                    Error: {error.message || "Failed to load premium payments"}
                </span>
            ) : (
                <SharedTable
                    type="premium-payments"
                    columns={columns}
                    items={items}
                    activeDraftId={null}
                    hasNextPage={hasNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    noResultsMessage="No premium payments match the current filters."
                    ref={ref}
                    disableModel
                    onDetailHandler={onDetailHandler}
                />
            )}
        </div>
    );
};

export default PremiumPayments;
